import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button, Space } from 'antd';
import { Link } from 'react-router-dom';
import { logger } from '../../services/logger.client';
import { ApiError } from '../../services/apiErrors';

/**
 * Props for the FeatureErrorBoundary component.
 */
interface FeatureErrorBoundaryProps {
  /** The name of the feature for context-aware error messages */
  feature: string;
  /** Child components to render */
  children: ReactNode;
}

/**
 * Internal state for error tracking.
 */
interface FeatureErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Route-level error boundary for feature isolation.
 *
 * Provides:
 * - Context-aware error messages using the `feature` prop
 * - Retry functionality via "Try Again" button (resets error state)
 * - Navigation back to dashboard for recovery
 * - Specialized handling for ApiError types
 * - Structured logging with feature context
 *
 * @see ADR-034 Container/Presenter Component Pattern
 *
 * @example
 * ```tsx
 * <Route path="/combat" element={
 *   <FeatureErrorBoundary feature="Combat">
 *     <CombatTrackerContainer />
 *   </FeatureErrorBoundary>
 * } />
 * ```
 */
class FeatureErrorBoundary extends Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
  public state: FeatureErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  /**
   * Updates state when an error is caught, triggering a re-render with fallback UI.
   */
  public static getDerivedStateFromError(error: Error): FeatureErrorBoundaryState {
    return { hasError: true, error };
  }

  /**
   * Logs the error with feature context for debugging and error tracking.
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error(
      {
        err: error,
        componentStack: errorInfo.componentStack,
        feature: this.props.feature,
      },
      `Error in ${this.props.feature} feature`
    );
  }

  /**
   * Resets the error state to allow retrying the failed operation.
   * This will re-render the children, giving them a chance to recover.
   */
  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  /**
   * Generates an appropriate subtitle message based on the error type.
   */
  private getSubTitle(): string {
    const { error } = this.state;
    const { feature } = this.props;

    if (error instanceof ApiError) {
      // Provide specific messages for different API error statuses
      switch (error.status) {
        case 401:
          return 'Your session has expired. Please log in again.';
        case 403:
          return 'You do not have permission to access this feature.';
        case 404:
          return `The requested ${feature.toLowerCase()} resource was not found.`;
        case 500:
        case 502:
        case 503:
          return 'The server is temporarily unavailable. Please try again in a moment.';
        default:
          return `API Error (${error.status}): ${error.message}`;
      }
    }

    if (error) {
      return error.message;
    }

    return `Something went wrong in the ${feature} feature. Please try again.`;
  }

  /**
   * Determines the Result component status based on error type.
   */
  private getResultStatus(): 'error' | 'warning' | '500' | '403' | '404' {
    const { error } = this.state;

    if (error instanceof ApiError) {
      switch (error.status) {
        case 403:
          return '403';
        case 404:
          return '404';
        case 500:
        case 502:
        case 503:
          return '500';
        default:
          return 'error';
      }
    }

    return 'error';
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      const { feature } = this.props;

      return (
        <Result
          status={this.getResultStatus()}
          title={`${feature} Error`}
          subTitle={this.getSubTitle()}
          extra={
            <Space>
              <Button type="primary" onClick={this.handleRetry}>
                Try Again
              </Button>
              <Link to="/">
                <Button>Return to Dashboard</Button>
              </Link>
            </Space>
          }
        />
      );
    }

    return this.props.children;
  }
}

export default FeatureErrorBoundary;
